/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DisaEntriesFormView.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Form view for creating and editing DISA Entries.
    Manages DISA configuration with General and Settings sections.

 🔧 Notes:
    - Refactored from monolithic 1033-line file
    - Uses hooks for state management
    - Uses section components for form layout
    - Maintains QuickSearchModal functionality

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PhoneCall, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useDisaEntryData } from './hooks/useDisaEntryData';
import { useDisaEntryForm } from './hooks/useDisaEntryForm';
import { GeneralSection, SettingsSection } from './components/DisaEntrySections';
import { deleteDisaEntryById, navigateToDisaEntryDuplicate } from './disaEntriesActions';
import { fetchDisaEntries } from './api/disaEntriesApi';
import { DisaEntry } from './types';

export default function DisaEntriesFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess } = useNotifications();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Quick List states
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListItems, setQuickListItems] = useState<DisaEntry[]>([]);
  const [initialItems, setInitialItems] = useState<DisaEntry[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isQuickSearching, setIsQuickSearching] = useState(false);

  const { isLoading, formData: loadedFormData, classOfServices } = useDisaEntryData(id, duplicateId);

  const {
    formData,
    errors,
    isSaving,
    hasChanges,
    handleChange,
    handleSettingsChange,
    handleSubmit,
    handleCancel
  } = useDisaEntryForm(loadedFormData, id, isEditMode);

  // Enhanced save handler
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      return { id, ...formData };
    }

    return savedData;
  }, [handleSubmit, formData, isEditMode, id]);

  // Use reusable hook for save actions
  const modeForSave = isEditMode ? 'edit' : 'create';
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/applications/disa',
    newPath: '/pbx/applications/disa/new',
    mode: modeForSave,
    getEditPath: (savedData) => `/pbx/applications/disa/${savedData.id || savedData.uuid}`,
    getFormData: () => formData,
    storageKey: 'disaEntries:saveAndNewData',
  });

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteDisaEntryById(id);

      showSuccess(t('disaEntries:messages.deleteSuccess'));
      navigate('/pbx/applications/disa');
    } catch (error) {
      // Error handled by deleteDisaEntryById
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [id, isEditMode, navigate, showSuccess, t]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToDisaEntryDuplicate(navigate, id);
  }, [isEditMode, id, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/applications/disa/new');
  }, [navigate]);

  // Quick List loader
  const loadInitialItems = useCallback(async () => {
    if (initialItems.length > 0) return;

    setIsLoadingInitial(true);
    try {
      const result = await fetchDisaEntries({
        page: 1,
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc'
      });
      if (result.success && Array.isArray(result.data)) {
        setInitialItems(result.data);
        setQuickListItems(result.data);
      }
    } catch (error) {
      console.error('Error loading initial items:', error);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [initialItems.length]);

  const loadQuickList = useCallback(
    async (search: string) => {
      const normalized = search.trim();

      if (!normalized || normalized.length < 2) {
        setQuickListItems(initialItems);
        return;
      }

      setIsQuickSearching(true);
      try {
        const result = await fetchDisaEntries({
          page: 1,
          limit: 50,
          sortBy: 'name',
          sortOrder: 'asc',
          search: normalized
        });
        if (result.success && Array.isArray(result.data)) {
          setQuickListItems(result.data);
        }
      } catch (error) {
        console.error('Error loading quick list items:', error);
      } finally {
        setIsQuickSearching(false);
      }
    },
    [initialItems]
  );

  useEffect(() => {
    if (showQuickList && initialItems.length === 0) {
      loadInitialItems();
    }
  }, [showQuickList, initialItems.length, loadInitialItems]);

  useEffect(() => {
    if (!showQuickList) return;

    const timer = setTimeout(() => {
      if (quickListSearch.trim().length >= 2) {
        loadQuickList(quickListSearch);
      } else {
        setQuickListItems(initialItems);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [quickListSearch, showQuickList, loadQuickList, initialItems]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <ModuleFormHeader
        icon={<PhoneCall className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('disaEntries:editTitle')} - ${formData.name || ''}` : t('disaEntries:createTitle')}
        backButtonText={t('disaEntries:actions.back')}
        onBack={() => navigate('/pbx/applications/disa')}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('disaEntries:actions.quickList'),
            onClick: () => setShowQuickList(true)
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('disaEntries:actions.duplicate'),
                onClick: handleDuplicateFromForm
              }
            ]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('disaEntries:actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <div className="mt-8">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
          className="space-y-6"
        >
          <GeneralSection
            formData={formData}
            errors={errors}
            classOfServices={classOfServices}
            onFieldChange={handleChange}
          />

          <SettingsSection
            formData={formData}
            errors={errors}
            onSettingsChange={handleSettingsChange}
          />
        </form>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
        isEditMode={isEditMode}
        isSaving={isSaving}
        deleteDisabled={isDeleting}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="disaEntries:saveAction"
        saveDisabled={isSaving}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('disaEntries:messages.deleteTitle')}
        message={t('disaEntries:messages.confirmDelete', { name: formData.name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('disaEntries:title')}
        items={quickListItems}
        isLoadingInitial={isLoadingInitial}
        isSearching={isQuickSearching}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/applications/disa/${item.uuid || item.id}`);
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            onClick={() => {
              if (!isCurrent) {
                setShowQuickList(false);
                setQuickListSearch('');
                navigate(`/pbx/applications/disa/${item.uuid || item.id}`);
              }
            }}
            disabled={isCurrent}
            className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'
              }`}
          >
            <div className="flex items-center gap-3">
              <PhoneCall className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-foreground">{item.context}</span>
                  {isCurrent && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                      {t('disaEntries.current', 'Current')}
                    </span>
                  )}
                </div>
                {item.name && (
                  <div className="text-sm text-muted-foreground truncate">{item.name}</div>
                )}
              </div>
            </div>
          </button>
        )}
        searchPlaceholder={t('common.searchPlaceholder')}
        minCharsMessage={t('disaEntries.quickListMinChars', 'Type at least 2 characters to search')}
        emptyMessage={t('disaEntries.quickListEmpty', 'Start typing to search DISA entries')}
        noResultsMessage={t('disaEntries.quickListNoResults', 'No DISA entries found')}
        headerIcon={<PhoneCall className="h-5 w-5" />}
        minSearchChars={2}
      />
    </div>
  );
}
